/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { createLogger } from '@/lib/logger';
import type { LivePersonTag } from './sdk';

const logger = createLogger();

export function readCookie(name: string, cookieHeader = document.cookie): string | null {
    const prefix = `${encodeURIComponent(name)}=`;
    const value = cookieHeader
        .split(';')
        .map((cookie) => cookie.trim())
        .find((cookie) => cookie.startsWith(prefix))
        ?.slice(prefix.length);
    if (!value) return null;

    try {
        return decodeURIComponent(value);
    } catch {
        logger.warn('[LivePerson] LPVID cookie could not be decoded; using its raw value');
        return value;
    }
}

/**
 * Resolves LPVID from the monitoring SDK first, then the LPVID cookie when
 * the externally managed tag is not ready or its SDK lookup fails.
 */
export async function resolveLivePersonVid(tag: LivePersonTag): Promise<string | null> {
    const monitoringSdk = tag.taglets?.lp_monitoringSDK;
    if (typeof monitoringSdk?.getVid === 'function') {
        try {
            const vid = await monitoringSdk.getVid();
            if (typeof vid === 'string' && vid.trim()) return vid.trim();
        } catch {
            logger.warn('[LivePerson] lp_monitoringSDK.getVid failed; checking LPVID cookie');
        }
    }

    return readCookie('LPVID');
}
